/**
 * @file   src\containers\Manager\Report\ShiftSummary.tsx
 * @brief  Shift Summary Report page
 * @date   Oct, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, Row, Col, Table, Button, useState, moment, useEffect, Dropdown } from '../../../components/ThirdPartyComponents';
import Strings from '../../../assests/strings/Strings.json';
import PDFIcon from '../../../assests/icons/PDF';
import KKIDatepicker from '../../../components/KKIDatepicker';
import { DATE_FORMAT_DD_MMM_YYYY, DATE_FORMAT_MM_DD_YYYY_FILTER, DATE_FORMAT_YYYY_MM_DD, HTTP_STATUS_200, NUMBER_0, NUMBER_1, NUMBER_25 } from '../../../utils/constants';
import { MessageToaster } from '../../../utils/toastUtils';
import { RootState } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { IshiftSummaryDetail, IShiftSummaryReportRequest } from '../../../interfaces/reportInterface';
import { exportShiftSummaryReportAsExcel, exportShiftSummaryReportAsPdf, getShiftSummaryReport, revertAllReportState } from '../../../store/actions/reportAction';
import Loader from '../../../components/Loader';
import { stringFormat } from '../../../utils/helper';
import Pagination from '../../../components/Paginate';
import EXPPDF from '../../../assests/icons/PDFIcon';
import EXPExcel from '../../../assests/icons/Excel';

// Toast object creation.
const toast = new MessageToaster();
// Define item count to be displayed per page.
const pageItemCount: number = NUMBER_25;
const ShiftSummary = () => {
  // Declare action dispatch.
  const dispatch = useAppDispatch();

  // Access redux state variables.
  const {
    isShiftSummaryReportLoading,
    isShiftSummaryReportSuccess,
    shiftSummaryReportErrorCode,
    shiftSummaryReportMessage,
    shiftSummaryReportData,
    totalShiftSummary,
    isExportShiftSummaryPdfLoading,
    isExportShiftSummaryPdfSuccess,
    exportShiftSummaryPdfErrorCode,
    exportShiftSummaryPdfMessage,
    shiftSummaryPdfData,
    isExportShiftSummaryExcelLoading,
    isExportShiftSummaryExcelSuccess,
    exportShiftSummaryExcelErrorCode,
    exportShiftSummaryExcelMessage,
    shiftSummaryExcelData,
  } = useAppSelector((state: RootState) => state.report);

  // Initialize component state variables.
  const [summaryDateSelected, setSummaryDateSelected] = useState<Date | null>(moment().toDate());
  const [pageSize] = useState<number>(pageItemCount);
  const [pageIndex, setPageIndex] = useState<number>(NUMBER_1);

  // Method to fetch shift summary api data.
  const fetchShiftSummary = () => {
    let summaryDate = '';
    if (summaryDateSelected) {
      summaryDate = moment(new Date(summaryDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
    }
    const reportRequest: IShiftSummaryReportRequest = {
      shiftDate: summaryDate,
      page: pageIndex,
      limit: pageSize,
    };
    dispatch(getShiftSummaryReport(reportRequest));
  };

  // Handle component side effect and fetch shift summary data.
  useEffect(() => {
    fetchShiftSummary();
    return () => {
      dispatch(revertAllReportState());
    };
  }, [pageIndex]);

  // Callout report fetch api response state change.
  useEffect(() => {
    if (!isShiftSummaryReportSuccess && shiftSummaryReportErrorCode > HTTP_STATUS_200 && shiftSummaryReportMessage) {
      toast.toastError(shiftSummaryReportMessage);
    }
  }, [isShiftSummaryReportSuccess, shiftSummaryReportErrorCode, shiftSummaryReportMessage]);

  // Export pdf api response state change handling.
  useEffect(() => {
    if (isExportShiftSummaryPdfSuccess && shiftSummaryPdfData) {
      const pdfBlob = new Blob([shiftSummaryPdfData], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Create a link element, set download attributes, and trigger a click
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.setAttribute('download', 'ShiftSummaryReport.pdf'); // Set the PDF filename
      document.body.appendChild(link);
      link.click();
      toast.toastSuccess(Strings.Text.ShiftSummaryExportSuccessText);
      // Cleanup: remove the link and revoke the URL
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
      dispatch(revertAllReportState());
    } else if (!isExportShiftSummaryPdfSuccess && exportShiftSummaryPdfErrorCode > HTTP_STATUS_200 && exportShiftSummaryPdfMessage) {
      toast.toastError(exportShiftSummaryPdfMessage);
      dispatch(revertAllReportState());
    }
  }, [isExportShiftSummaryPdfSuccess, exportShiftSummaryPdfErrorCode, exportShiftSummaryPdfMessage, shiftSummaryPdfData]);

  // Export excel api response state change handling.
  useEffect(() => {
    if (isExportShiftSummaryExcelSuccess && shiftSummaryExcelData) {
      const excelBlob = new Blob([shiftSummaryExcelData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const excelUrl = URL.createObjectURL(excelBlob);

      // Create a link element, set download attributes, and trigger a click
      const link = document.createElement('a');
      link.href = excelUrl;
      link.setAttribute('download', 'ShiftSummaryReport.xlsx'); // Set the Excel filename
      document.body.appendChild(link);
      link.click();
      toast.toastSuccess(Strings.Text.ShiftSummaryExportSuccessText);
      // Cleanup: remove the link and revoke the URL
      document.body.removeChild(link);
      URL.revokeObjectURL(excelUrl);
      dispatch(revertAllReportState());
    } else if (!isExportShiftSummaryExcelSuccess && exportShiftSummaryExcelErrorCode > HTTP_STATUS_200 && exportShiftSummaryExcelMessage) {
      toast.toastError(exportShiftSummaryExcelMessage);
      dispatch(revertAllReportState());
    }
  }, [isExportShiftSummaryExcelSuccess, exportShiftSummaryExcelErrorCode, exportShiftSummaryExcelMessage, shiftSummaryExcelData]);

  // Summary date input select event handler.
  const handleDateChange = (date: Date | null) => {
    setSummaryDateSelected(date);
  };

  // Generate report button click handler.
  const onGenerateReport = () => {
    if (pageIndex !== NUMBER_1) {
      setPageIndex(NUMBER_1);
    } else {
      fetchShiftSummary();
    }
  };

  // Handle the "Export as PDF" button click event.
  const handleExportPDFClick = (): void => {
    let summaryDate = '';
    if (summaryDateSelected) {
      summaryDate = moment(new Date(summaryDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
    }
    dispatch(exportShiftSummaryReportAsPdf({ shiftDate: summaryDate, page: NUMBER_0 }));
  };

  // Handle the "Export as Excel" button click event.
  const handleExportExcelClick = (): void => {
    let summaryDate = '';
    if (summaryDateSelected) {
      summaryDate = moment(new Date(summaryDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
    }
    dispatch(exportShiftSummaryReportAsExcel({ shiftDate: summaryDate, page: NUMBER_0 }));
  };

  // Set shift summary header.
  const setShiftSummaryHeader = (dt: string | undefined) => {
    if (!dt) {
      return '';
    }
    return `${moment.utc(dt).format(DATE_FORMAT_DD_MMM_YYYY)}`;
  };

  return (
    <div>
      <div className="pageheader">
        <Row className="align-items-center justify-content-between">
          <Col>
            <h1>{Strings.HD.ShiftSummary}</h1>
          </Col>
        </Row>
      </div>
      <div className="filter-main mb-3">
        <Row className="d-flex justify-content-between">
          <Col xl={4} md={6} sm={12}>
            <Row className="align-items-center">
              <Col xs="auto" className="pe-0">
                {Strings.Label.ShiftDate}
              </Col>
              <Col xs className="calendar-mob-right">
                <KKIDatepicker
                  id="summaryDate"
                  name="summaryDate"
                  placeholder={Strings.Label.ShiftDate}
                  value={summaryDateSelected}
                  dateFormat={DATE_FORMAT_MM_DD_YYYY_FILTER}
                  onChangeDatepicker={(date: any) => handleDateChange(date)}
                />
              </Col>
              <Col md="auto" className="ps-0">
                <Button variant="primary" type="button" onClick={() => onGenerateReport()}>
                  {Strings.Button.GenerateReport}
                </Button>
              </Col>
            </Row>
          </Col>
          <Col md="auto">
            <Dropdown className="exportfile">
              <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
                <PDFIcon />
                {Strings.Button.Export}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item href={void 0} onClick={handleExportPDFClick}>
                  <EXPPDF />
                  {Strings.Button.PDF}
                </Dropdown.Item>
                <Dropdown.Item href={void 0} onClick={handleExportExcelClick}>
                  <EXPExcel />
                  {Strings.Button.Excel}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
      </div>
      <h6 className="fw-sb">{setShiftSummaryHeader(shiftSummaryReportData?.shiftDate)} Summary</h6>
      <div className="table-border mb-4">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>{Strings.Label.DaySummary}</th>
              <th>{Strings.Label.EveningSummary}</th>
              <th>{Strings.Label.NightSummary}</th>
            </tr>
          </thead>
          <tbody>
            {shiftSummaryReportData && Object.keys(shiftSummaryReportData).length > NUMBER_0 && (
              <tr>
                <td>{`${shiftSummaryReportData.daySummary || NUMBER_0}/${shiftSummaryReportData.daySummaryRequired || NUMBER_0}`}</td>
                <td>{`${shiftSummaryReportData.eveningSummary || NUMBER_0}/${shiftSummaryReportData.eveningSummaryRequired || NUMBER_0}`}</td>
                <td>{`${shiftSummaryReportData.nightSummary || NUMBER_0}/${shiftSummaryReportData.nightSummaryRequired || NUMBER_0}`}</td>
              </tr>
            )}
            {(!shiftSummaryReportData || (shiftSummaryReportData && Object.keys(shiftSummaryReportData)?.length === NUMBER_0)) && (
              <tr>
                <td colSpan={3} className="text-center">
                  {Strings.Text.NoDataFound}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <h6 className="fw-sb">{setShiftSummaryHeader(shiftSummaryReportData?.shiftDate)} Detailed Report</h6>
      <div className="table-border mb-4">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>{Strings.Label.ShiftCode}</th>
              <th>{Strings.Label.CAName}</th>
              <th>{Strings.Label.NormalCA}</th>
              <th>{Strings.Label.OnCallCA}</th>
            </tr>
          </thead>
          <tbody>
            {shiftSummaryReportData?.shifts && shiftSummaryReportData?.shifts?.length > NUMBER_0 ? (
              shiftSummaryReportData?.shifts?.map((item: IshiftSummaryDetail, index) => (
                <React.Fragment key={`${item.shiftName}-${index}`}>
                  <tr key={`${item.shiftName}-${index}`}>
                    <td>{`${item.shiftName} (${item.shortName})`}</td>
                    <td>{item.caName}</td>
                    <td>{item.isNormalCa ? 'Y' : '-'}</td>
                    <td>{item.isOnCallCa ? 'Y' : '-'}</td>
                  </tr>
                  {(() => {
                    if (item.showTotals) {
                      const shiftTotal = shiftSummaryReportData.shiftTotals?.find((tot) => tot.shortName === item.shortName);
                      return (
                        shiftTotal && (
                          <tr className="shiftsummary-total">
                            <td>{`${shiftTotal?.shortName} Total`}</td>
                            <td>{shiftTotal.totalCas}</td>
                            <td>{shiftTotal.normalCas}</td>
                            <td>{shiftTotal.onCalCas}</td>
                          </tr>
                        )
                      );
                    }
                    return null;
                  })()}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center">
                  {Strings.Text.NoDataFound}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-between mt-4">
        <span>
          {(() => {
            const displayText = '';
            if (shiftSummaryReportData && shiftSummaryReportData?.shifts?.length > NUMBER_0 && totalShiftSummary > 0) {
              const callOut = totalShiftSummary === NUMBER_1 ? NUMBER_1 : pageIndex;
              const start = (callOut - NUMBER_1) * pageSize + NUMBER_1;
              const end = (callOut - NUMBER_1) * pageSize + shiftSummaryReportData.shifts.length;
              return stringFormat(Strings.Text.ShiftSummaryReportPaginationText, start, end, totalShiftSummary);
            }
            return displayText;
          })()}
        </span>
        {shiftSummaryReportData && shiftSummaryReportData?.shifts?.length > NUMBER_0 && totalShiftSummary > pageSize && (
          <Pagination totalCount={totalShiftSummary} pageLimit={pageSize} setCurrentPage={(page: number) => setPageIndex(page)} currentPage={pageIndex - NUMBER_1} prevPage={-1} />
        )}
      </div>
      {(isShiftSummaryReportLoading || isExportShiftSummaryPdfLoading || isExportShiftSummaryExcelLoading) && <Loader />}
    </div>
  );
};
export default ShiftSummary;
